import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import {
  Users, Calendar, Target, TrendingUp, BarChart3, Clock, Filter,
  Loader2, AlertCircle, Check, X, Clock4, MapPin, Building,
  ChevronDown, ChevronUp, User, Activity, BookOpen, Network, CircleDollarSign,  Download,
  FileSpreadsheet,
  FileText


} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
// Color schemes matching event analytics
const CHART_COLORS = {
  primary: {
    purple: ['#7C3AED', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE'],
    green: ['#059669', '#10B981', '#34D399', '#6EE7B7', '#A7F3D0'],
    orange: ['#EA580C', '#F97316', '#FB923C', '#FDBA74', '#FED7AA'],
    blue: ['#2563EB', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE']
  }
};

const CARD_SCHEMES = {
  purple: {
    background: 'bg-purple-50',
    icon: 'bg-purple-100 text-purple-600',
    accent: 'text-purple-600',
    border: 'border-purple-200',
  },
  green: {
    background: 'bg-green-50',
    icon: 'bg-green-100 text-green-600',
    accent: 'text-green-600',
    border: 'border-green-200',
  },
  orange: {
    background: 'bg-orange-50',
    icon: 'bg-orange-100 text-orange-600',
    accent: 'text-orange-600',
    border: 'border-orange-200',
  },
  blue: {
    background: 'bg-blue-50',
    icon: 'bg-blue-100 text-blue-600',
    accent: 'text-blue-600',
    border: 'border-blue-200',
  }
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'fundraising', label: 'Fundraising', icon: CircleDollarSign },
  { id: 'contributors', label: 'Contributors', icon: Users },
  { id: 'performance', label: 'Performance', icon: Activity }
];

const formatCurrency = (value) => {
  if (!value && value !== 0) return '₹0';
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
  return `₹${value.toFixed(0)}`;
};

// MetricCard component matching event analytics style
const MetricCard = ({ title, value, icon: Icon, trend, subtext, scheme = "purple" }) => {
  const colors = CARD_SCHEMES[scheme];
  
  return (
    <div className={`${colors.background} rounded-2xl p-6 transition-all duration-300 
      hover:shadow-lg hover:scale-[1.02] relative overflow-hidden group border ${colors.border}`}
    >
      <div className="relative flex justify-between items-start">
        <div className={`p-3 rounded-xl ${colors.icon} transition-all duration-300 
          group-hover:scale-110`}>
          <Icon className="h-6 w-6" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center ${trend >= 0 ? 'text-green-600' : 'text-red-600'} 
            bg-white/80 px-2 py-1 rounded-lg`}>
            <span className="text-sm font-medium">
              {trend > 0 ? '+' : ''}{trend}%
            </span>
            {trend >= 0 ? (
              <ChevronUp className="h-4 w-4 ml-1" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-1" />
            )}
          </div>
        )}
      </div>
      <div className="relative mt-4">
        <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        <p className="text-gray-600 text-sm mt-1 font-medium">{title}</p>
        {subtext && (
          <p className={`text-sm ${colors.accent} mt-2`}>{subtext}</p>
        )}
      </div>
    </div>
  );
};

// ChartCard component matching event analytics style
const ChartCard = ({ title, subtitle, children, icon: Icon, scheme = "purple" }) => {
  const colors = CARD_SCHEMES[scheme];
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all border border-gray-100">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-lg ${colors.background}`}>
            <Icon className={`h-5 w-5 ${colors.accent}`} />
          </div>
        )}
      </div>
      {children}
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow border border-gray-100">
        <p className="font-medium text-gray-900">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm mt-1">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-500">{entry.name}:</span>
            <span className="font-medium text-gray-900">
              {entry.name.toLowerCase().includes('amount') 
                ? formatCurrency(entry.value)
                : entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};
const CampaignAnalytics = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('365');
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, [timeRange]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/campaigns`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Failed to fetch campaigns');
      const data = await response.json();
      setCampaigns(data.filter(campaign => campaign.status !== 'rejected'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600" />
          <p className="text-purple-600 font-medium">Loading campaign analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <div className="bg-red-100 rounded-full p-3 w-12 h-12 mx-auto mb-4">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Analytics</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchCampaigns}
            className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 
              transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Calculate analytics data
  const analytics = {
    totalRaised: campaigns.reduce((sum, camp) => sum + (camp.financials?.raisedAmount || 0), 0),
    totalTarget: campaigns.reduce((sum, camp) => sum + (camp.financials?.targetAmount || 0), 0),
    activeCampaigns: campaigns.filter(c => c.status === 'published').length,
    totalDonors: campaigns.reduce((sum, camp) => sum + (camp.donations?.length || 0), 0),
    successRate: campaigns.length 
      ? (campaigns.reduce((sum, camp) => 
          sum + ((camp.financials?.raisedAmount || 0) / (camp.financials?.targetAmount || 1) * 100), 0) 
          / campaigns.length
        ).toFixed(1)
      : 0,
    categories: campaigns.reduce((acc, camp) => {
      if (camp.category) {
        acc[camp.category] = (acc[camp.category] || 0) + 1;
      }
      return acc;
    }, {})
  };

  // Process monthly data
  const monthlyData = campaigns
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    .map(camp => ({
      month: new Date(camp.createdAt).toLocaleString('default', { month: 'short' }),
      raised: camp.financials?.raisedAmount || 0,
      target: camp.financials?.targetAmount || 0,
      donations: camp.donations?.length || 0,
      success: camp.financials?.targetAmount 
        ? ((camp.financials.raisedAmount || 0) / camp.financials.targetAmount * 100).toFixed(1)
        : 0
    }));
// Function to prepare data for export
const prepareExportData = () => {
  return {
    overview: [
      ['Metric', 'Value'],
      ['Total Raised', formatCurrency(analytics.totalRaised)],
      ['Total Target', formatCurrency(analytics.totalTarget)],
      ['Active Campaigns', analytics.activeCampaigns],
      ['Total Donors', analytics.totalDonors],
      ['Success Rate', `${analytics.successRate}%`]
    ],
    
    campaigns: [
      ['Campaign', 'Category', 'Raised', 'Target', 'Success Rate', 'Donors'],
      ...campaigns.map(campaign => [
        campaign.title,
        campaign.category,
        formatCurrency(campaign.financials?.raisedAmount || 0),
        formatCurrency(campaign.financials?.targetAmount || 0),
        `${((campaign.financials?.raisedAmount || 0) / (campaign.financials?.targetAmount || 1) * 100).toFixed(1)}%`,
        campaign.donations?.length || 0
      ])
    ],
    
    donations: [
      ['Campaign', 'Donor', 'Type', 'Amount', 'Status'],
      ...campaigns.flatMap(camp => 
        camp.donations.map(donation => [
          camp.title,
          `${donation.contributor.firstName} ${donation.contributor.lastName}`,
          donation.type,
          donation.type === 'money' ? formatCurrency(donation.amount) : 'N/A',
          donation.status
        ])
      )
    ],
    
    performance: [
      ['Category', 'Success Rate', 'Avg Duration', 'Total Raised'],
      ...Object.entries(analytics.categories).map(([category, count]) => {
        const categoryCampaigns = campaigns.filter(c => c.category === category);
        return [
          category,
          `${(categoryCampaigns.reduce((sum, c) => 
            sum + ((c.financials?.raisedAmount || 0) / (c.financials?.targetAmount || 1) * 100), 0) / count).toFixed(1)}%`,
          `${Math.round(categoryCampaigns.reduce((sum, c) => {
            const start = new Date(c.timeline.startDate);
            const end = new Date(c.timeline.endDate);
            return sum + (end - start) / (1000 * 60 * 60 * 24);
          }, 0) / count)} days`,
          formatCurrency(categoryCampaigns.reduce((sum, c) => sum + (c.financials?.raisedAmount || 0), 0))
        ];
      })
    ]
  };
};

// Function to export to Excel
const exportToExcel = () => {
  const data = prepareExportData();
  const wb = XLSX.utils.book_new();
  
  // Add each section as a worksheet
  Object.entries(data).forEach(([sheetName, sheetData]) => {
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(wb, ws, sheetName.charAt(0).toUpperCase() + sheetName.slice(1));
  });

  // Save the workbook
  XLSX.writeFile(wb, `campaign_analytics_${new Date().toLocaleDateString()}.xlsx`);
};

// Function to export to PDF
const exportToPDF = () => {
  const data = prepareExportData();
  const doc = new jsPDF();
  
  // Set initial position
  let yPos = 20;
  
  // Add title
  doc.setFontSize(16);
  doc.text('Campaign Analytics Report', 20, yPos);
  yPos += 10;
  
  // Add date and filters
  doc.setFontSize(10);
  doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, yPos);
  doc.text(`Time Range: ${timeRange} days`, 20, yPos + 5);
  yPos += 20;
  
  // Add sections
  Object.entries(data).forEach(([section, rows]) => {
    // Add section header
    doc.setFontSize(14);
    doc.setTextColor(82, 82, 255);
    doc.text(section.charAt(0).toUpperCase() + section.slice(1), 20, yPos);
    yPos += 10;
    
    // Reset text color
    doc.setTextColor(0);
    doc.setFontSize(10);
    
    // Add table data
    rows.forEach((row) => {
      // Check if we need a new page
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.text(row.join(' | '), 30, yPos);
      yPos += 7;
    });
    
    yPos += 10;
  });
  
  // Save the PDF
  doc.save(`campaign_analytics_${new Date().toLocaleDateString()}.pdf`);
};

// Replace the existing filter button with export dropdown
const exportButton = (
  <div className="relative">
    <button
      onClick={() => setShowExportMenu(!showExportMenu)}
      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 
        hover:bg-white/20 transition-colors border border-purple-400/30 
        text-white group"
    >
      <Download className="h-5 w-5 group-hover:scale-110 transition-transform" />
      <span>Export</span>
      <ChevronDown className="h-4 w-4" />
    </button>

    {showExportMenu && (
      <div className="absolute right-0 mt-2 w-48 rounded-lg bg-white shadow-lg 
        ring-1 ring-black ring-opacity-5 z-50"
        onBlur={() => setShowExportMenu(false)}
      >
        <div className="py-1">
          <button
            onClick={() => {
              exportToExcel();
              setShowExportMenu(false);
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 
              hover:bg-gray-100 w-full text-left"
          >
            <FileSpreadsheet className="h-4 w-4 text-green-600" />
            Export as Excel
          </button>
          <button
            onClick={() => {
              exportToPDF();
              setShowExportMenu(false);
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 
              hover:bg-gray-100 w-full text-left"
          >
            <FileText className="h-4 w-4 text-blue-600" />
            Export as PDF
          </button>
        </div>
      </div>
    )}
  </div>
);
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 pb-32 pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white">Campaign Analytics</h1>
              <p className="mt-2 text-purple-100">
                Comprehensive insights into campaign performance and metrics
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative">
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="appearance-none bg-white/10 text-white border border-purple-400/30 
                    rounded-lg pl-4 pr-10 py-2 focus:ring-2 focus:ring-white/20 focus:outline-none"
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last quarter</option>
                  <option value="180">Last 6 months</option>
                  <option value="365">Last year</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/70" />
              </div>

              <div className="flex gap-2">
      {exportButton}
      <button className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors
        border border-purple-400/30 text-white group">
        <Filter className="h-5 w-5 group-hover:scale-110 transition-transform" />
      </button>
      <button 
        onClick={fetchCampaigns}
        className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors
          border border-purple-400/30 text-white group"
      >
        <Activity className="h-5 w-5 group-hover:scale-110 transition-transform" />
      </button>
    </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-12 border-b border-purple-400/30">
            <div className="flex gap-8 overflow-x-auto pb-px">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 pb-4 text-sm font-medium whitespace-nowrap
                    border-b-2 transition-all duration-300
                    ${activeTab === tab.id
                      ? 'text-white border-white'
                      : 'text-purple-100 border-transparent hover:text-white hover:border-white/50'
                    }
                  `}
                >
                  <tab.icon className={`h-4 w-4 ${
                    activeTab === tab.id ? 'text-white' : 'text-purple-100'
                  }`} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="-mt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <MetricCard
                    title="Total Raised"
                    value={formatCurrency(analytics.totalRaised)}
                    icon={CircleDollarSign}
                    trend={15.8}
                    subtext="Total funds raised"
                    scheme="purple"
                  />
                  <MetricCard
                    title="Active Campaigns"
                    value={analytics.activeCampaigns}
                    icon={Target}
                    trend={7.2}
                    subtext="Currently running"
                    scheme="green"
                  />
                  <MetricCard
                    title="Total Donors"
                    value={analytics.totalDonors}
                    icon={Users}
                    trend={12.5}
                    subtext="Unique contributors"
                    scheme="orange"
                  />
                  <MetricCard
                    title="Success Rate"
                    value={`${analytics.successRate}%`}
                    icon={Activity}
                    trend={5.7}
                    subtext="Goal completion"
                    scheme="blue"
                  />
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Fundraising Progress */}
                  <ChartCard 
                    title="Fundraising Progress" 
                    subtitle="Monthly fundraising patterns"
                    icon={CircleDollarSign}
                    scheme="purple"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={monthlyData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis tickFormatter={formatCurrency} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="raised"
                            name="Amount Raised"
                            stroke={CHART_COLORS.primary.purple[0]}
                            fill={CHART_COLORS.primary.purple[0]}
                            fillOpacity={0.3}
                          />
                          <Area
                            type="monotone"
                            dataKey="target"
                            name="Target Amount"
                            stroke={CHART_COLORS.primary.blue[0]}
                            fill={CHART_COLORS.primary.blue[0]}
                            fillOpacity={0.3}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>

                  {/* Category Distribution */}
                  <ChartCard 
                    title="Category Distribution" 
                    subtitle="Campaigns by category"
                    icon={Radar}
                    scheme="green"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={Object.entries(analytics.categories).map(([name, value]) => ({
                              name,
                              value
                            }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {Object.entries(analytics.categories).map((_, index) => (
                              <Cell 
                                key={`cell-${index}`}
                                fill={CHART_COLORS.primary[
                                  Object.keys(CHART_COLORS.primary)[index % 
                                    Object.keys(CHART_COLORS.primary).length]
                                ][0]}
                              />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>
                </div>

                {/* Recent Campaigns */}
                <ChartCard 
                  title="Recent Campaigns" 
                  subtitle="Latest campaign activities"
                  icon={Target}
                  scheme="purple"
                >
                  <div className="space-y-4">
                    {campaigns.slice(0, 5).map((campaign, index) => (
                      <div key={campaign._id} 
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl
                          hover:bg-gray-100 transition-all group"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-lg ${
                            CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].background
                          }`}>
                            <Target className={`h-5 w-5 ${
                              CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].accent
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{campaign.title}</p>
                            <p className="text-sm text-gray-500">
                              {formatCurrency(campaign.financials?.raisedAmount || 0)} raised
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {((campaign.financials?.raisedAmount || 0) / 
                              (campaign.financials?.targetAmount || 1) * 100).toFixed(1)}%
                          </p>
                          <p className="text-sm text-gray-500">{campaign.category}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ChartCard>
              </>
            )}

{/* Fundraising Tab */}
{activeTab === 'fundraising' && (
              <div className="space-y-6">
                {/* Fundraising Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <MetricCard
                    title="Total Target"
                    value={formatCurrency(analytics.totalTarget)}
                    icon={Target}
                    subtext="Combined goals"
                    scheme="purple"
                  />
                  <MetricCard
                    title="Avg. Donation"
                    value={formatCurrency(
                      analytics.totalRaised / (analytics.totalDonors || 1)
                    )}
                    icon={CircleDollarSign}
                    subtext="Per donor"
                    scheme="green"
                  />
                  <MetricCard
                    title="Funding Gap"
                    value={formatCurrency(
                      Math.max(0, analytics.totalTarget - analytics.totalRaised)
                    )}
                    icon={TrendingUp}
                    subtext="Amount remaining"
                    scheme="orange"
                  />
                </div>

                {/* Monthly Fundraising */}
                <ChartCard 
                  title="Monthly Fundraising" 
                  subtitle="Progress over time"
                  icon={CircleDollarSign}
                  scheme="purple"
                >
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={formatCurrency} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="raised"
                          name="Amount Raised"
                          stroke={CHART_COLORS.primary.purple[0]}
                          fill={CHART_COLORS.primary.purple[0]}
                          fillOpacity={0.3}
                        />
                        <Line
                          type="monotone"
                          dataKey="target"
                          name="Target"
                          stroke={CHART_COLORS.primary.orange[0]}
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </ChartCard>

                {/* Campaign Progress List */}
                <ChartCard 
                  title="Campaign Progress" 
                  subtitle="Individual campaign performance"
                  icon={Target}
                  scheme="blue"
                >
                  <div className="space-y-4">
                    {campaigns.map((campaign, index) => (
                      <div key={campaign._id} 
                        className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all"
                      >
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${
                              CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].background
                            }`}>
                              <Target className={`h-4 w-4 ${
                                CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].accent
                              }`} />
                            </div>
                            <h4 className="font-medium text-gray-900">{campaign.title}</h4>
                          </div>
                          <span className="text-sm text-gray-500">
                            {campaign.category}
                          </span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Progress</span>
                            <span className="font-medium text-gray-900">
                              {formatCurrency(campaign.financials?.raisedAmount || 0)} / 
                              {formatCurrency(campaign.financials?.targetAmount || 0)}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full bg-blue-500 transition-all duration-500"
                              style={{
                                width: `${Math.min(100, ((campaign.financials?.raisedAmount || 0) / 
                                  (campaign.financials?.targetAmount || 1)) * 100)}%`
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ChartCard>
              </div>
            )}

            {/* Contributors Tab */}
            {activeTab === 'contributors' && (
              <div className="space-y-6">
                {/* Contributor Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <MetricCard
                    title="Total Contributors"
                    value={analytics.totalDonors}
                    icon={Users}
                    subtext="Unique donors"
                    scheme="purple"
                  />
                  <MetricCard
                    title="Avg. Contributions"
                    value={formatCurrency(
                      analytics.totalRaised / (analytics.totalDonors || 1)
                    )}
                    icon={CircleDollarSign}
                    subtext="Per contributor"
                    scheme="green"
                  />
                  <MetricCard
                    title="Active Donors"
                    value={campaigns.reduce((sum, camp) => 
                      sum + (camp.donations?.filter(d => d.status === 'approved')?.length || 0), 0
                    )}
                    icon={Activity}
                    subtext="Approved donations"
                    scheme="orange"
                  />
                </div>

                {/* Contribution Trends */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Monthly Contributions */}
                  <ChartCard 
                    title="Monthly Contributions" 
                    subtitle="Donation patterns over time"
                    icon={Activity}
                    scheme="purple"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={monthlyData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar 
                            dataKey="donations" 
                            name="Number of Donations"
                            fill={CHART_COLORS.primary.purple[0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>

                  {/* Contribution Distribution */}
                  <ChartCard 
                    title="Contribution Types" 
                    subtitle="Distribution by donation type"
                    icon={Radar}
                    scheme="green"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              {
                                name: 'Money',
                                value: campaigns.reduce((sum, camp) => 
                                  sum + (camp.donations?.filter(d => d.type === 'money')?.length || 0), 0)
                              },
                              {
                                name: 'Skills',
                                value: campaigns.reduce((sum, camp) => 
                                  sum + (camp.donations?.filter(d => d.type === 'skills')?.length || 0), 0)
                              },
                              {
                                name: 'Resources',
                                value: campaigns.reduce((sum, camp) => 
                                  sum + (camp.donations?.filter(d => d.type === 'resource')?.length || 0), 0)
                              }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {[0, 1, 2].map((index) => (
                              <Cell 
                                key={`cell-${index}`}
                                fill={CHART_COLORS.primary[
                                  Object.keys(CHART_COLORS.primary)[index]
                                ][0]}
                              />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>
                </div>

                {/* Recent Contributions */}
                <ChartCard 
                  title="Recent Contributions" 
                  subtitle="Latest donation activities"
                  icon={Users}
                  scheme="blue"
                >
                  <div className="space-y-4">
                    {campaigns
                      .flatMap(camp => 
                        camp.donations.map(donation => ({
                          ...donation,
                          campaignTitle: camp.title
                        }))
                      )
                      .slice(0, 5)
                      .map((donation, index) => (
                        <div key={index} 
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-lg ${
                              CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].background
                            }`}>
                              <User className={`h-5 w-5 ${
                                CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].accent
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {donation.contributor.firstName} {donation.contributor.lastName}
                              </p>
                              <p className="text-sm text-gray-500">{donation.campaignTitle}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              {donation.type === 'money' 
                                ? formatCurrency(donation.amount)
                                : donation.type}
                            </p>
                            <p className={`text-sm ${
                              donation.status === 'approved' ? 'text-green-600' : 'text-yellow-600'
                            }`}>
                              {donation.status}
                            </p>
                          </div>
                        </div>
                    ))}
                  </div>
                </ChartCard>
              </div>
            )}

            {/* Performance Tab */}
            {activeTab === 'performance' && (
              <div className="space-y-6">
                {/* Performance Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <MetricCard
                    title="Success Rate"
                    value={`${analytics.successRate}%`}
                    icon={Target}
                    subtext="Goal completion"
                    scheme="purple"
                  />
                  <MetricCard
                    title="Active Rate"
                    value={`${((analytics.activeCampaigns / campaigns.length) * 100).toFixed(1)}%`}
                    icon={Activity}
                    subtext="Campaign activity"
                    scheme="green"
                  />
                  <MetricCard
                    title="Avg. Duration"
                    value={`${Math.round(campaigns.reduce((sum, camp) => {
                      const start = new Date(camp.timeline.startDate);
                      const end = new Date(camp.timeline.endDate);
                      return sum + (end - start) / (1000 * 60 * 60 * 24);
                    }, 0) / campaigns.length)} days`}
                    icon={Clock}
                    subtext="Campaign length"
                    scheme="orange"
                  />
                </div>

                {/* Performance Analysis */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Success Rate by Category */}
                  <ChartCard 
                    title="Category Performance" 
                    subtitle="Success rates by category"
                    icon={Target}
                    scheme="purple"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart cx="50%" cy="50%" outerRadius="80%">
                          <PolarGrid />
                          <PolarAngleAxis dataKey="category" />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} />
                          <Radar
                            name="Success Rate"
                            dataKey="value"
                            stroke={CHART_COLORS.primary.purple[0]}
                            fill={CHART_COLORS.primary.purple[0]}
                            fillOpacity={0.6}
                            data={Object.entries(analytics.categories).map(([category, count]) => ({
                              category,
                              value: (campaigns
                                .filter(c => c.category === category)
                                .reduce((sum, c) => sum + 
                                  ((c.financials?.raisedAmount || 0) / 
                                   (c.financials?.targetAmount || 1) * 100), 0) / count
                              ).toFixed(1)
                            }))}
                          />
                          <Legend />
                          <Tooltip content={<CustomTooltip />} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>

                  {/* Timeline Performance */}
                  <ChartCard 
                    title="Campaign Timeline" 
                    subtitle="Duration and success correlation"
                    icon={Clock}
                    scheme="green"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={campaigns.map(camp => ({
                            name: camp.title,
                            duration: Math.round((new Date(camp.timeline.endDate) - 
                              new Date(camp.timeline.startDate)) / (1000 * 60 * 60 * 24)),
                            success: ((camp.financials?.raisedAmount || 0) / 
                              (camp.financials?.targetAmount || 1) * 100).toFixed(1)
                          }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis yAxisId="left" label={{ value: 'Duration (days)', angle: -90 }} />
                          <YAxis yAxisId="right" orientation="right" label={{ value: 'Success (%)', angle: 90 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar yAxisId="left" dataKey="duration" name="Duration" fill={CHART_COLORS.primary.green[0]} />
                          <Line yAxisId="right" type="monotone" dataKey="success" name="Success Rate" 
                            stroke={CHART_COLORS.primary.orange[0]} />
                            </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>
                </div>

                {/* Campaign Performance Details */}
                <ChartCard 
                  title="Campaign Performance Details" 
                  subtitle="Detailed success metrics"
                  icon={Activity}
                  scheme="blue"
                >
                  <div className="space-y-4">
                    {campaigns.map((campaign, index) => {
                      const successRate = ((campaign.financials?.raisedAmount || 0) / 
                        (campaign.financials?.targetAmount || 1) * 100).toFixed(1);
                      const duration = Math.round((new Date(campaign.timeline.endDate) - 
                        new Date(campaign.timeline.startDate)) / (1000 * 60 * 60 * 24));
                      
                      return (
                        <div key={campaign._id} 
                          className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all"
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${
                                CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].background
                              }`}>
                                <Activity className={`h-4 w-4 ${
                                  CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].accent
                                }`} />
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900">{campaign.title}</h4>
                                <p className="text-sm text-gray-500">{campaign.category}</p>
                              </div>
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              successRate >= 80 
                                ? 'bg-green-50 text-green-600' 
                                : successRate >= 50 
                                  ? 'bg-yellow-50 text-yellow-600'
                                  : 'bg-red-50 text-red-600'
                            }`}>
                              {successRate}% Complete
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div>
                              <p className="text-sm text-gray-500">Duration</p>
                              <p className="font-medium text-gray-900">{duration} days</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Contributors</p>
                              <p className="font-medium text-gray-900">
                                {campaign.donations?.length || 0}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Progress</span>
                              <span className="font-medium text-gray-900">
                                {formatCurrency(campaign.financials?.raisedAmount || 0)} / 
                                {formatCurrency(campaign.financials?.targetAmount || 0)}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="h-2 rounded-full transition-all duration-500"
                                style={{
                                  width: `${Math.min(100, Number(successRate))}%`,
                                  backgroundColor: successRate >= 80 
                                    ? CHART_COLORS.primary.green[0]
                                    : successRate >= 50
                                      ? CHART_COLORS.primary.orange[0]
                                      : CHART_COLORS.primary.purple[0]
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ChartCard>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignAnalytics;